import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import React from "react";
import { Link } from "react-router-dom";
import productService from "../../api/services/product.service";
import { getProductImages } from "../../utils/imageUtils";
import { useCart } from "../../context/CartContext";
import { useToast } from "../../admin/context/ToastContext";
import { useSession } from "../../context/SessionContext";
import "./ProductDetails.css";
import "../../styles/Layout.css";
import "../Home/Home.css";

const quantityOptions = [
  { label: "12 Shots", value: "12", multiplier: 1 },
  { label: "36 Shots", value: "36", multiplier: 3 },
  { label: "3 Shots", value: "3", multiplier: 0.25 }
];

const deliveryOptions = [
  { label: "3 months", value: "3-months" },
  { label: "2 months", value: "2-months" },
  { label: "1 month", value: "1-month" },
  { label: "3 weeks", value: "3-weeks" },
  { label: "2 weeks", value: "2-weeks" },
  { label: "1 week", value: "1-week" }
];

/** Value after ":" — keep dose in the table row; move prose after an en/em/ASCII dash to a line below. */
function splitFormulaDosageAndNote(valueAfterColon) {
  const val = String(valueAfterColon || "").trim();
  if (!val) return { dosage: "", note: "" };
  const m = val.match(
    /^(.+?\b(?:mg|g|ml|mcg|iu)\b(?:\s*\([^)]*\))?)\s*[\u2013\u2014\u2212–—\-]\s*(.+)$/i
  );
  if (m) return { dosage: m[1].trim(), note: m[2].trim() };
  return { dosage: val, note: "" };
}

export default function ProductDetails() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart, isProductInCart, getCartItemForProduct, openSidebar, removeFromCart } = useCart();
  const { showToast } = useToast();
  const { userUuid, ipAddress } = useSession(); // Use the corrected identifiers
  const [purchaseType, setPurchaseType] = useState("one-time");
  const [selectedQuantity, setSelectedQuantity] = useState("");
  const [deliveryFrequency, setDeliveryFrequency] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await productService.view(id);
        const data = res?.data || res?.product || res;
        
        // Normalize product data
        const normalized = {
          id: data.uuid || data._id || data.product_code || data.id,
          name: data.name,
          description: data.description,
          howToUse: data.how_to_use,
          price: data.price || 0,
          originalPrice: data.original_price,
          images: getProductImages(data),
          variants: data.variants || [],
          inStock: data.stock > 0,
          category: data.category_details?.name || ""
        };
        
        setProduct(normalized);
      } catch (err) {
        console.error("Failed to fetch product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const getVariantPrice = (variant) => {
    if (!variant) return NaN;
    return Number(variant.variant_price ?? variant.price ?? NaN);
  };

  const validVariants = useMemo(() => {
    if (!product?.variants) return [];
    return product.variants.filter((variant) => {
      const price = getVariantPrice(variant);
      const hasValidPrice = Number.isFinite(price) && price > 0;
      
      // If subscription is enabled, validate billing durations
      const hasValidSubscription = !variant.is_subscription_enable || (
        Array.isArray(variant.billing_durations) && 
        variant.billing_durations.length > 0 &&
        variant.billing_durations.every(duration => Number.isFinite(duration) && duration > 0)
      );
      
      return hasValidPrice && hasValidSubscription;
    }).sort((a, b) => {
      // Sort A-Z by quantity/attribute value
      const getQuantity = (variant) => {
        if (Array.isArray(variant.attributes)) {
          return variant.attributes[0]?.attribute_value || '';
        }
        return Object.values(variant.attributes || {})[0] || '';
      };
      
      const aValue = getQuantity(a);
      const bValue = getQuantity(b);
      
      // Convert to numbers if possible for proper numeric sorting
      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum; // Numeric ascending
      }
      
      // Fallback to string comparison
      return aValue.localeCompare(bValue);
    });
  }, [product?.variants]);

  const getSelectedVariant = () => {
    if (!validVariants || validVariants.length === 0) return null;
    return validVariants.find((v) => {
      if (Array.isArray(v.attributes)) {
        return v.attributes.some((attr) => String(attr.attribute_value) === selectedQuantity);
      }
      return v.attributes && Object.values(v.attributes).some((val) => String(val) === selectedQuantity);
    }) || validVariants[0];
  };

  const currentVariant = getSelectedVariant();

  // Calculate derived values needed for useEffect hooks - Add null checks
  const basePrice = product ? Number(product.price ?? 0) : 0;
  const rawVariantPrice = currentVariant ? Number(currentVariant.variant_price ?? currentVariant.price ?? 0) : 0;
  const variantPrice = Number.isFinite(rawVariantPrice) && rawVariantPrice > 0
    ? rawVariantPrice
    : Number.isFinite(basePrice) && basePrice > 0 ? basePrice : 0;
  const discountPercent = currentVariant?.discount_percentage || 0;

  const canSubscribe = Boolean(
    currentVariant?.is_subscription_enable &&
    Array.isArray(currentVariant?.billing_durations) &&
    currentVariant.billing_durations.length > 0 &&
    currentVariant.billing_durations.every(duration => Number.isFinite(duration) && duration > 0)
  );

  const dynamicDeliveryOptions = useMemo(() => {
    return canSubscribe
      ? currentVariant.billing_durations.map(d => ({
          label: d >= 30 ? `${Math.floor(d/30)} month${d >= 60 ? 's' : ''}` : `${Math.floor(d/7)} week${d >= 14 ? 's' : ''}`,
          value: `${d}-days`
        }))
      : deliveryOptions;
  }, [canSubscribe, currentVariant?.billing_durations]);

  const dynamicQuantityOptions = useMemo(() => {
    return validVariants.length > 0
      ? validVariants.map(v => {
          const val = Array.isArray(v.attributes)
            ? v.attributes[0]?.attribute_value
            : Object.values(v.attributes || {})[0] || "12";
          return { label: `${val} Shots`, value: String(val), multiplier: 1 };
        })
      : quantityOptions;
  }, [validVariants]);

  // Move all useEffect hooks after variable declarations
  useEffect(() => {
    const sourceVariants = validVariants.length > 0 ? validVariants : product?.variants || [];
    if (sourceVariants.length > 0) {
      const firstVariant = sourceVariants[0];
      const quantityAttr = Array.isArray(firstVariant.attributes)
        ? firstVariant.attributes[0]?.attribute_value
        : Object.values(firstVariant.attributes || {})[0];

      if (quantityAttr) {
        setSelectedQuantity(String(quantityAttr));
      }
    } else {
      setSelectedQuantity("12");
    }
  }, [product, validVariants]);

  useEffect(() => {
    if (purchaseType === "subscribe" && !canSubscribe) {
      setPurchaseType("one-time");
    }
  }, [canSubscribe, purchaseType]);



  if (loading) {
    return (
      <div className="products-loading-full">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  // Check if product has no valid variants with complete data
  if (!product || validVariants.length === 0) {
    return (
      <>
        {/* Large Logo Section - Same as Home */}
        <div style={{
          position: 'fixed',
          top: '16px',
          left: '24px',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.filter = 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.4))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.filter = 'none';
            }}
          >
          </Link>
        </div>

        <div style={{ marginTop: '120px' }}>
          <section className="premium-section section-padding" style={{
            position: 'relative',
            minHeight: '60vh',
            overflow: 'hidden',
          }}>
            {/* Cinematic Blue Overlay - Same as Home */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(11, 15, 26, 0.3) 0%, rgba(17, 22, 42, 0.25) 50%, rgba(26, 31, 58, 0.2) 100%)',
              zIndex: 1,
            }} />
            
            {/* Light Silver Mist - Luxury Effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle at 30% 20%, rgba(0, 255, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 255, 255, 0.06) 0%, transparent 50%)',
              zIndex: 2,
            }} />
            
            {/* Premium Grain Overlay */}
            <div className="grain-overlay" style={{ zIndex: 3 }} />
            
            <div className="premium-container" style={{ position: 'relative', zIndex: 4 }}>
              <div className="fade-in-up section-header">
                <h1 className="section-heading" style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3rem)',
                  fontWeight: 800,
                  margin: '0 0 1.5rem 0',
                  fontFamily: 'var(--font-display)',
                  textTransform: 'uppercase',
                  letterSpacing: '-0.03em',
                  color: '#F8FAFC',
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 0 25px rgba(232,236,243,0.3)',
                }}>
                  <span style={{
                    color: '#00FFFF',
                    textShadow: '0 4px 12px rgba(0, 0, 0, 0.5), 0 0 25px rgba(0, 255, 255, 0.5)',
                  }}>Product</span> Unavailable
                </h1>
                
                <div className="premium-divider" style={{
                  width: '120px',
                  height: '3px',
                  margin: '2.5rem auto',
                  background: '#00FFFF',
                  boxShadow: '0 0 20px rgba(0, 255, 255, 0.4)',
                }} />
                
                <div className="fade-in-up glass-panel" style={{
                  maxWidth: '700px',
                  margin: '0 auto 3rem auto',
                  padding: '2rem'
                }}>
                  <p className="body-text" style={{
                    fontSize: 'clamp(1.125rem, 2vw, 1.25rem)',
                    color: '#FFFFFF',
                    lineHeight: 1.8,
                    margin: 0,
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    textAlign: 'center',
                    textShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
                  }}>
                    {!product ? "The product you're looking for doesn't exist or has been removed." : "This product is currently unavailable as it has no valid purchasing options."}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
    );
  }


  // Use product images from data or fallbacks
  const productImages = product.images && product.images.length > 0
    ? product.images
    : [
        "/chilltree-images/chill-tree-gold.png",
        "/chilltree-images/chill-tree-green.png", 
        "/chilltree-images/chill-tree-red.png",
        "/chilltree-images/gold-rush.png"
      ];

  const handleAddToCart = async () => { // Make async
    // Validate required fields
    if (!product || !product.id) {
      console.error("Product is not available");
      return;
    }

    if (purchaseType === "subscribe" && !deliveryFrequency) {
      showToast("Please select a delivery frequency for your subscription.", "info");
      return;
    }

    const variantUuid = currentVariant?.variant_uuid || currentVariant?.uuid;
    if (!variantUuid) {
      console.error("Variant is not selected");
      return;
    }

    if (!userUuid && !ipAddress) {
      console.error("Session identifier is missing");
      return;
    }

    setIsAdding(true);

    const billingDuration =
      purchaseType === "subscribe" && deliveryFrequency
        ? parseInt(String(deliveryFrequency).split("-")[0], 10)
        : undefined;

    const currentPurchaseType = purchaseType === "subscribe" ? "subscription" : "one_time";

    try {
      if (shouldShowUpdateButton && cartItem) {
        // Update existing cart item by removing old and adding new
        await removeFromCart(cartItem.id, true); // Silent removal during update
        
        // Add new item to cart with updated properties
        const payload = {
          item_uuid: product.id,
          variant_uuid: variantUuid,
          quantity: 1,
          purchase_type: currentPurchaseType,
        };

        // Only add user_uuid if it exists
        if (userUuid) {
          payload.user_uuid = userUuid;
        }

        // Only add ip_address if it exists
        if (ipAddress) {
          payload.ip_address = ipAddress;
        }

        // Only add billing_duration if it exists and is valid
        if (billingDuration && billingDuration > 0) {
          payload.billing_duration = billingDuration;
        }

        await addToCart(payload);
        openSidebar();
      } else {
        // Add new item to cart
        const payload = {
          item_uuid: product.id,
          variant_uuid: variantUuid,
          quantity: 1,
          purchase_type: currentPurchaseType,
        };

        // Only add user_uuid if it exists
        if (userUuid) {
          payload.user_uuid = userUuid;
        }

        // Only add ip_address if it exists
        if (ipAddress) {
          payload.ip_address = ipAddress;
        }

        // Only add billing_duration if it exists and is valid
        if (billingDuration && billingDuration > 0) {
          payload.billing_duration = billingDuration;
        }

        await addToCart(payload); // Pass payload to context
        openSidebar();
      }
    } catch (error) {
      // Error is already handled in CartContext with toast
      console.error("Failed to update cart:", error);
    } finally {
      setIsAdding(false);
    }
  };

  // Check if current product variant is in cart
  const variantUuid = currentVariant?.variant_uuid || currentVariant?.uuid;
  const isInCart = product ? isProductInCart(product.id, variantUuid) : false;
  const cartItem = product && variantUuid ? getCartItemForProduct(product.id, variantUuid) : null;

  // Check if subscription options have changed from what's in cart
  const hasSubscriptionChanges = () => {
    if (!cartItem) return false;
    
    const currentPurchaseType = purchaseType === "subscribe" ? "subscription" : "one_time";
    const purchaseTypeChanged = cartItem.purchaseType !== currentPurchaseType;
    
    let durationChanged = false;
    if (currentPurchaseType === "subscription") {
      const currentDuration = deliveryFrequency ? parseInt(String(deliveryFrequency).split("-")[0], 10) : null;
      durationChanged = Number(cartItem.billingDuration) !== Number(currentDuration);
    }
    
    return purchaseTypeChanged || durationChanged;
  };

  const shouldShowUpdateButton = isInCart && hasSubscriptionChanges();

  const handleViewCart = () => {
    openSidebar();
  };

  const parseStructuredDescription = (desc) => {
    if (!desc) return null;
    const lines = String(desc).split(/\n+/).map(l => l.trim()).filter(l => l.length > 0);
    const sections = {
      intro: [],
      formula: [],
      benefits: [],
      flavor: [],
      directions: [],
      bestFor: [],
      seo: [],
    };

    let currentSection = 'intro';
    lines.forEach((line) => {
      const lower = line.toLowerCase();
      if (lower.includes('botanical formula') || lower.includes('formula (per')) {
        currentSection = 'formula';
      } else if (lower.includes('key benefits')) {
        currentSection = 'benefits';
      } else if (lower.includes('flavor profile')) {
        currentSection = 'flavor';
      } else if (lower.includes('directions for use')) {
        currentSection = 'directions';
      } else if (lower.includes('best for')) {
        currentSection = 'bestFor';
      } else if (lower.includes('seo') || lower.includes('amazon indexing')) {
        currentSection = 'seo';
      } else {
        sections[currentSection].push(line);
      }
    });
    return sections;
  };

  const structuredDesc = parseStructuredDescription(product.description);

  const rawSnippetSource = String(product?.description || '').trim();
  const snippetWords = rawSnippetSource.split(/\s+/).filter(Boolean);
  const shortDescriptionSnippet =
    snippetWords.length === 0
      ? ''
      : snippetWords.length <= 36
        ? rawSnippetSource
        : `${snippetWords.slice(0, 36).join(' ')}…`;

  const formatReviews = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <span className="stars">
        {Array(fullStars).fill("★").map((star, i) => (
          <span key={`full-${i}`} className="star full">
            {star}
          </span>
        ))}
        {hasHalfStar && <span className="star half">★</span>}
        {Array(emptyStars).fill("☆").map((star, i) => (
          <span key={`empty-${i}`} className="star empty">
            {star}
          </span>
        ))}
      </span>
    );
  };

  const oneTimePrice = Number.isFinite(variantPrice) && variantPrice > 0 ? variantPrice.toFixed(2) : "--";
  const subscribePrice = Number.isFinite(variantPrice) && variantPrice > 0
    ? (variantPrice * (1 - discountPercent / 100)).toFixed(2)
    : "--";
  const savePercent = discountPercent;


  return (
    <>
      {/* Large Logo Section - Same as Home */}
      <div style={{
        position: 'fixed',
        top: '16px',
        left: '24px',
        zIndex: 999,
        display: 'flex',
        alignItems: 'center'
      }}>
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.filter = 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.4))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.filter = 'none';
          }}
        >
        </Link>
      </div>

      <div className="product-details-container full-width">
        <div className="product-details-grid-new">
          <div className="product-gallery-section pdp-grid-gallery">
              <div className="product-main-image-container">
                <button 
                  className="image-nav-btn prev-btn"
                  onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? productImages.length - 1 : prev - 1))}
                  aria-label="Previous image"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <img
                  src={productImages[currentImageIndex]}
                  alt={product.name}
                  className="product-main-image"
                />
                <button 
                  className="image-nav-btn next-btn"
                  onClick={() => setCurrentImageIndex((prev) => (prev === productImages.length - 1 ? 0 : prev + 1))}
                  aria-label="Next image"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              {/* Thumbnail Gallery */}
              <div className="product-thumbnail-row">
                {productImages.map((image, index) => (
                  <button
                    key={index}
                    className={`product-thumbnail ${currentImageIndex === index ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            </div>

          <aside className="product-purchase-right sticky-col pdp-grid-purchase" aria-label="Purchase options">
            <div className="sticky-inner">
              <h1 className="product-title-new">
                {product.name}
              </h1>

              {product.rating && product.reviews && (
                <div className="product-rating-meta">
                  {renderStars(product.rating)}
                  <span className="rating-number">
                    {product.rating}
                  </span>
                  <span className="reviews-count">
                    ({formatReviews(product.reviews)} reviews)
                  </span>
                </div>
              )}

              {/* Price Block */}
              <div className="product-price-new">
                {purchaseType === "subscribe" ? (
                  <div className="price-row">
                    <span className="current-price">${subscribePrice}</span>
                    <span className="old-price-new">${oneTimePrice}</span>
                    <span className="discount-tag">{savePercent}% OFF</span>
                  </div>
                ) : (
                  <span className="current-price">${oneTimePrice}</span>
                )}
              </div>

              {shortDescriptionSnippet ? (
                <div className="short-desc-snippet">
                  {shortDescriptionSnippet}
                </div>
              ) : null}

              {/* Quantity Selector */}
              <div className="quantity-selector-new">
                <h3 className="selector-label">CHOOSE PACK QUANTITY:</h3>
                <div className="quantity-grid-new">
                  {dynamicQuantityOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`qty-pill ${selectedQuantity === option.value ? "active" : ""}`}
                      onClick={() => setSelectedQuantity(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Purchase Options */}
              <div className="purchase-options-new">
                {/* One-time option */}
                <div
                  className={`option-box ${purchaseType === "one-time" ? "active" : ""}`}
                  onClick={() => setPurchaseType("one-time")}
                >
                  <div className="option-radio">
                    <input type="radio" checked={purchaseType === "one-time"} readOnly />
                    <span className="option-name">ONE-TIME</span>
                  </div>
                  <span className="option-price">${oneTimePrice}</span>
                </div>

                {/* Subscription option */}
                <div
                  className={`option-box subscribe-box-new ${purchaseType === "subscribe" ? "active" : ""} ${!canSubscribe ? "disabled" : ""}`}
                  onClick={() => canSubscribe && setPurchaseType("subscribe")}
                  aria-disabled={!canSubscribe}
                >
                  {savePercent > 0 && <span className="save-badge-new">Save up to {savePercent}%</span>}
                  
                  <div className="option-header-row">
                    <div className="option-radio">
                      <input type="radio" checked={purchaseType === "subscribe"} readOnly disabled={!canSubscribe} />
                      <span className="option-name">SUBSCRIBE & SAVE</span>
                    </div>
                    <div className="option-price-stack">
                      <span className="old-price-sub">${oneTimePrice}</span>
                      <span className="option-price">${subscribePrice}</span>
                    </div>
                  </div>

                  {!canSubscribe && (
                    <div className="subscription-disabled-note">
                      Subscription is not available for this variant.
                    </div>
                  )}

                  {purchaseType === "subscribe" && canSubscribe && (
                    <div className="sub-details-clean">
                      <ul className="sub-benefits-mini-new">
                        <li>✓ {savePercent}% off every order!</li>
                        <li>✓ Manage your subscription anytime via SMS.</li>
                        <li>✓ Free shipping on all orders</li>
                      </ul>
                      <div className="freq-selector">
                        <p className="freq-label">Deliver every:</p>
                        <div className="freq-grid-new">
                          {dynamicDeliveryOptions.map(option => (
                            <button
                              key={option.value}
                              className={`freq-pill-new ${deliveryFrequency === option.value ? "selected" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeliveryFrequency(option.value);
                              }}
                            >
                              <span className="freq-name">{option.label}</span>
                              <span className="freq-save">save {savePercent}%</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add to Cart Button */}
              {isInCart && !shouldShowUpdateButton ? (
                <button
                  type="button"
                  onClick={handleViewCart}
                  className="atc-btn in-cart"
                >
                  ✓ IN CART - VIEW CART
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAddToCart}
                  className={`atc-btn ${isAdding ? 'adding' : ''} ${shouldShowUpdateButton ? 'update-btn' : ''}`}
                  disabled={isAdding || !currentVariant || !product || (!userUuid && !ipAddress)}
                >
                  {isAdding ? 'UPDATING!' : shouldShowUpdateButton ? 'UPDATE CART' : 'ADD TO CART'}
                </button>
              )}

              <div className="find-in-store-link">
                <Link to="/stores">FIND IN STORE</Link>
              </div>
            </div>
          </aside>

            <div className="product-detailed-info pdp-grid-details" id="overview">
              {structuredDesc && structuredDesc.intro.length > 0 && (
                <div className="content-section">
                  <h2 className="section-title">PRODUCT OVERVIEW</h2>
                  {structuredDesc.intro.map((p, i) => (
                    <p key={i} className={`content-text ${i === 0 ? 'intro-lead' : ''}`}>{p}</p>
                  ))}
                </div>
              )}

              {structuredDesc && structuredDesc.benefits.length > 0 && (
                <div className="content-section">
                  <h2 className="section-title">KEY BENEFITS</h2>
                  <ul className="clean-list">
                    {structuredDesc.benefits.map((benefit, i) => (
                      <li key={i} className="clean-list-item">
                        <span className="check-icon">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {structuredDesc && structuredDesc.formula.length > 0 && (
                <div className="content-section">
                  <h2 className="section-title">BOTANICAL FORMULA</h2>
                  {/* Separate the introductory paragraph if it exists */}
                  {structuredDesc.formula[0] && !structuredDesc.formula[0].includes(':') && (
                    <p className="content-text formula-intro">{structuredDesc.formula[0]}</p>
                  )}
                  <div className="clean-formula-grid">
                    {structuredDesc.formula.slice(structuredDesc.formula[0] && !structuredDesc.formula[0].includes(':') ? 1 : 0).map((item, i) => {
                      const ci = item.indexOf(':');
                      const name = ci === -1 ? item : item.slice(0, ci).trim();
                      const rawVal = ci === -1 ? '' : item.slice(ci + 1).trim();
                      const { dosage, note } = splitFormulaDosageAndNote(rawVal);
                      return (
                        <div key={i} className="formula-item-wrap">
                          <div className="formula-item">
                            <span className="ingredient">{name}</span>
                            <span className="dosage">{dosage}</span>
                          </div>
                          {note ? <p className="formula-item-note">{note}</p> : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {structuredDesc && structuredDesc.flavor.length > 0 && (
                <div className="content-section">
                  <h2 className="section-title">FLAVOR PROFILE</h2>
                  {structuredDesc.flavor.map((p, i) => (
                    <p key={i} className="content-text">{p}</p>
                  ))}
                </div>
              )}

              {structuredDesc && structuredDesc.bestFor?.length > 0 && (
                <div className="content-section">
                  <h2 className="section-title">BEST FOR</h2>
                  <ul className="clean-list">
                    {structuredDesc.bestFor.map((line, i) => (
                      <li key={i} className="clean-list-item">
                        <span className="check-icon">✓</span>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {structuredDesc && structuredDesc.seo?.length > 0 && (
                <div className="content-section content-section-muted">
                  <h2 className="section-title section-title-sub">KEYWORDS & DISCOVERABILITY</h2>
                  {structuredDesc.seo.map((line, i) => (
                    <p key={i} className="content-text pdp-seo-line">{line}</p>
                  ))}
                </div>
              )}

              {(structuredDesc?.directions?.length > 0 || String(product.howToUse || '').trim().length > 0) && (
                <div className="content-section pdp-safety-stack">
                  {structuredDesc?.directions?.length > 0 && (
                    <>
                      <h2 className="section-title">DIRECTIONS FOR USE</h2>
                      <ul className="clean-list">
                        {structuredDesc.directions.map((line, i) => (
                          <li key={i} className="clean-list-item">{line}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {String(product.howToUse || '').trim().length > 0 && (
                    <>
                      <h2
                        className={`section-title${structuredDesc?.directions?.length > 0 ? ' section-title-spaced' : ''}`}
                      >
                        SAFETY & COMPLIANCE
                      </h2>
                      <div className="pdp-compliance-card" role="region" aria-label="Safety and compliance information">
                        <div className="pdp-compliance-pre">{String(product.howToUse).trim()}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>
    </>
  );
}



